import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MultipleStopOutlinedIcon from "@mui/icons-material/MultipleStopOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Button from "@mui/material/Button";

import ImageInput from "../../components/form/ImageInput";
import Images from "./Images/Images";
import ImagesScrubber from "./Images/ImagesScrubber";
import ImagesOrders from "./Images/ImagesOrders";
import useAuth from "../../hooks/useAuth";
import ImagesItem from "./Images/ImagesItem";
import ImagesInternal from "./Images/ImagesInternal";
import ImagesOutbound from "./Images/ImagesOutbound";
import { useAssetsCollectionSafe } from "../../contexts/assetsCollection";
import useImageUploadWithAttachments from "../../hooks/useImageUploadWithAttachments";
import useQueryParams from "../../hooks/useQueryParams";
import FormField from "./FormField";
import AssetLabelingOptionsModal from "../../components/modals/AssetLabelingOptionsModal";
import LabelingOptionsModal from "../../components/modals/LabelingOptionsModal";
import useItemLabelsModal from "../../hooks/useItemLabelsModal";

export function getCostCenterValue(costCenter) {
  return costCenter
    ? `${costCenter.costCenterName || ""} ${costCenter.costCenterCode || ""}`.trim()
    : "";
}

export default function FieldsIdentifiers({
  fullWidth,
  type,
  disabledForm,
  asset,
  displayClient,
  displayProject,
  displayWarehouse,
  internalWorkOrderId,
  fields = [],
  onReorder,
  display = {},
}) {
  const { user } = useAuth();
  const params = useQueryParams();
  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";
  const md = fullWidth ? 12 : 6;
  const { settings } = user.company;
  const isItem = type === "item";
  const isInternal = type === "internal";
  const itemLabelsModal = useItemLabelsModal();
  const shipmentItem = asset?.shipmentItems?.[0];
  const hasShipmentItem = Boolean(shipmentItem?.shipment?.id);
  const matchesShipmentItemLabelsModal =
    hasShipmentItem &&
    `${itemLabelsModal.shipmentId || ""}` === `${shipmentItem.shipment.id}` &&
    `${itemLabelsModal.shipmentItemId || ""}` === `${shipmentItem.id}`;

  const assetsCollection = useAssetsCollectionSafe();
  const bulkCreateImages = assetsCollection?.bulkCreateImages;
  const handleBulkCreateImagesWithAttachments = useImageUploadWithAttachments(
    asset,
    bulkCreateImages
  );

  const handleCreateImage = (file, { onSuccess, onFailure } = {}) => {
    return handleBulkCreateImagesWithAttachments({
      variables: { assetId: asset.id, images: [file] }, // Wrap single file in array
      onSuccess: (result) => {
        // Extract the single image from the bulk result for backwards compatibility
        const image = result?.images?.[0];
        const asset = result?.asset;
        if (onSuccess) {
          onSuccess({ image, asset });
        }
      },
      onFailure,
    });
  };

  return (
    <Card style={{ overflow: "visible" }}>
      <CardContent>
        <Box
          mt={-1}
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={500} fontSize={18}>
              Identifiers
            </Typography>
            {isReceivingWizardDisabled && (
              <Chip
                label="Receiving Assistant Disabled"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {onReorder && (
              <Tooltip arrow title="Reorder fields">
                <IconButton onClick={onReorder}>
                  <MultipleStopOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Grid container alignItems="flex-start" spacing={2}>
          <Grid size={{ lg: fullWidth ? 12 : 5, md: md, xs: 12 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                {type === "scrubber" && <ImagesScrubber asset={asset} />}{" "}
                {/* Image viewer for asset details modal */}
                {type === "capture" && <Images asset={asset} />}{" "}
                {/* Image viewer for capture page */}
                {type === "item" && <ImagesItem asset={asset} />}
                {type === "orders" && <ImagesOrders disabledForm={disabledForm} asset={asset} />}
                {type === "internal" && (
                  <ImagesInternal asset={asset} internalWorkOrderId={internalWorkOrderId} />
                )}
                {(type === "outbound" || type === "delivery") && (
                  <ImagesOutbound asset={asset} disabledForm={disabledForm} />
                )}
              </Grid>
              {!isInternal && !isItem && !disabledForm && (
                <>
                  <Grid size={12}>
                    <ImageInput imageUrl={asset.photo} onUpload={handleCreateImage} allowLibrary />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          <Grid size={{ lg: fullWidth ? 12 : 7, md: md, xs: 12 }}>
            <Grid container spacing={1.5}>
              {displayClient && (
                <Grid size={md}>
                  <TextField
                    fullWidth
                    size="small"
                    label={settings.filter_field1}
                    value={asset.client.name}
                    sx={{
                      fieldset: { borderColor: "primary.main" },
                      label: { color: "primary.main" },
                      input: { color: "primary.main" },
                    }}
                  />
                </Grid>
              )}
              {displayProject && (
                <Grid size={md}>
                  <TextField
                    fullWidth
                    size="small"
                    label={settings.filter_field2}
                    value={asset.project.name}
                    sx={{
                      fieldset: { borderColor: "primary.main" },
                      label: { color: "primary.main" },
                      input: { color: "primary.main" },
                    }}
                  />
                </Grid>
              )}
              {displayWarehouse && (
                <Grid size={md}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Current Warehouse"
                    value={
                      asset.warehousesFromRackLocations
                        ?.map((warehouse) => warehouse.name)
                        .join(", ") || ""
                    }
                    sx={{
                      fieldset: { borderColor: "primary.main" },
                      label: { color: "primary.main" },
                      input: { color: "primary.main" },
                    }}
                  />
                </Grid>
              )}

              {fields.map(({ id, params, type, gridProps }) => (
                <Grid key={id} size={{ xs: 12, md: gridProps?.sm || md }}>
                  <FormField id={id} params={params} type={type} disabled={disabledForm} />
                </Grid>
              ))}
              {display.asset_label_pdf && asset?.id && (
                <Grid size={{ xs: 12, md }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() =>
                      hasShipmentItem
                        ? itemLabelsModal.openForShipment(shipmentItem.shipment.id, shipmentItem.id)
                        : itemLabelsModal.openForAsset(asset.id)
                    }
                    startIcon={<PictureAsPdfIcon />}
                    sx={{ color: "text.secondary", borderColor: "divider" }}
                  >
                    Item Label PDF
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      {itemLabelsModal.isOpen && matchesShipmentItemLabelsModal && (
        <LabelingOptionsModal
          onClose={itemLabelsModal.close}
          shipmentId={shipmentItem.shipment.id}
          shipmentItemId={shipmentItem.id}
          project={asset.project}
        />
      )}
      {itemLabelsModal.isOpen && itemLabelsModal.assetId === asset?.id && (
        <AssetLabelingOptionsModal
          assetId={asset.id}
          project={asset.project}
          onClose={itemLabelsModal.close}
        />
      )}
    </Card>
  );
}
